/**
 * An equilibrium equation as the reader types it, turned into species with coefficients.
 *
 * Unlike a stoichiometry problem, K belongs to the equation *as written*: K for
 * ½N₂ + ½O₂ ⇌ NO is the square root of K for N₂ + O₂ ⇌ 2NO. So coefficients typed in front
 * of a formula are used exactly (whole numbers, decimals or fractions; a formula with none
 * counts as 1), and must balance the equation. If no coefficient is typed at all, the
 * equation is balanced to the smallest whole numbers. (s) and (l) mark a pure solid or
 * liquid, which has no place in K.
 */

import {
  balance,
  type EquationError,
  isBalancedWith,
  parseEquation,
  type Species,
} from "@/lib/tool-kit/chem-reactions/balance";
import type { EquilibriumSpecies } from "@/lib/tool-kit/chem-reactions/equilibrium";
import { superscriptInt } from "@/lib/tool-kit/chemistry/format";

export type ReactionRead =
  | {
      ok: true;
      species: Species[];
      equilibrium: EquilibriumSpecies[];
      /** The coefficients were worked out, not typed. */
      balancedForYou: boolean;
    }
  | { ok: false; reason: "parse"; error: EquationError }
  | { ok: false; reason: "unbalanced" | "noSolutes" };

function isSolid(species: Species): boolean {
  return species.state === "s" || species.state === "l";
}

function typedCoefficients(species: readonly Species[]): number[] | null {
  if (species.every((item) => item.typedCoefficient === null)) {
    return null;
  }
  return species.map((item) => item.typedCoefficient ?? 1);
}

export function reactionFromEquation(text: string): ReactionRead {
  const parsed = parseEquation(text);
  if (!parsed.ok) {
    return { ok: false, reason: "parse", error: parsed.error };
  }
  const { equation } = parsed;
  let coefficients = typedCoefficients(equation.species);
  const typed = coefficients !== null;
  if (coefficients === null) {
    const outcome = balance(equation);
    if (
      outcome.kind !== "unique" ||
      !outcome.coefficients.every((value) => value > 0)
    ) {
      return { ok: false, reason: "unbalanced" };
    }
    coefficients = outcome.coefficients;
  } else if (!isBalancedWith(equation, coefficients)) {
    return { ok: false, reason: "unbalanced" };
  }
  const equilibrium = equation.species.map((item, index) => ({
    label: item.label,
    coefficient: coefficients[index],
    side: item.side,
    solid: isSolid(item),
  }));
  if (equilibrium.every((item) => item.solid)) {
    return { ok: false, reason: "noSolutes" };
  }
  return {
    ok: true,
    species: equation.species,
    equilibrium,
    balancedForYou: !typed,
  };
}

function power(label: string, coefficient: number): string {
  if (coefficient === 1) {
    return `[${label}]`;
  }
  if (Number.isInteger(coefficient)) {
    return `[${label}]${superscriptInt(coefficient)}`;
  }
  return `[${label}]^${coefficient}`;
}

function product(species: readonly EquilibriumSpecies[]): string {
  return species.length === 0
    ? "1"
    : species.map((item) => power(item.label, item.coefficient)).join("");
}

/** `K = [HI]² ÷ ([H₂][I₂])` — solids left out. */
export function kExpression(species: readonly EquilibriumSpecies[]): string {
  const live = species.filter((item) => !item.solid);
  const top = product(live.filter((item) => item.side === "product"));
  const bottomItems = live.filter((item) => item.side === "reactant");
  const bottom = product(bottomItems);
  if (bottomItems.length === 0) {
    return `K = ${top}`;
  }
  return bottomItems.length > 1
    ? `K = ${top} ÷ (${bottom})`
    : `K = ${top} ÷ ${bottom}`;
}

/* --------------------------------------------- the expression with numbers */

function exponent(coefficient: number): string {
  if (coefficient === 1) {
    return "";
  }
  return Number.isInteger(coefficient)
    ? superscriptInt(coefficient)
    : `^${coefficient}`;
}

function joinTerms(terms: readonly string[]): string {
  if (terms.length === 0) {
    return "1";
  }
  return terms.join(" × ");
}

function wrap(terms: readonly string[]): string {
  return terms.length > 1 ? `(${joinTerms(terms)})` : joinTerms(terms);
}

function substitute(
  species: readonly EquilibriumSpecies[],
  term: (item: EquilibriumSpecies, index: number) => string
): string {
  const top: string[] = [];
  const bottom: string[] = [];
  for (const [index, item] of species.entries()) {
    if (item.solid) {
      continue;
    }
    const text = `${term(item, index)}${exponent(item.coefficient)}`;
    (item.side === "product" ? top : bottom).push(text);
  }
  return bottom.length === 0
    ? joinTerms(top)
    : `${wrap(top)} ÷ ${wrap(bottom)}`;
}

/** K with each concentration written as a number: (0.202)² ÷ ((0.399) × (1.197)³). */
export function kWithValues(
  species: readonly EquilibriumSpecies[],
  values: readonly string[]
): string {
  return substitute(species, (_item, index) => `(${values[index]})`);
}

/**
 * K with each concentration written as its ICE-table entry: (2x)² ÷ ((1.00 − x) × (1.00 − x)).
 * `initial` holds the starting concentrations already formatted for display.
 */
export function kWithExtent(
  species: readonly EquilibriumSpecies[],
  initial: readonly string[],
  zero: readonly boolean[]
): string {
  return substitute(species, (item, index) => {
    const size = item.coefficient === 1 ? "x" : `${item.coefficient}x`;
    if (item.side === "product") {
      const bare = item.coefficient === 1 ? size : `(${size})`;
      return zero[index] ? bare : `(${initial[index]} + ${size})`;
    }
    return zero[index] ? `(−${size})` : `(${initial[index]} − ${size})`;
  });
}
